/**
 * React Query hooks for scenario operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getScenarios,
  getScenario,
  createScenario,
  updateScenario,
  deleteScenario,
  forkScenario,
} from '../api/scenarios';

/** Query key factory for scenarios. */
export const scenarioKeys = {
  all: (filters) => ['scenarios', filters],
  lists: () => ['scenarios'],
  detail: (id) => ['scenarios', 'detail', id],
};

/**
 * List scenarios with optional filters.
 * @param {Object} [filters={}] - { community_id?, status? }
 */
export function useScenarios(filters = {}) {
  return useQuery({
    queryKey: scenarioKeys.all(filters),
    queryFn: () => getScenarios(filters),
  });
}

/**
 * Get a single scenario by ID.
 * @param {string|null} scenarioId - UUID
 */
export function useScenario(scenarioId) {
  return useQuery({
    queryKey: scenarioKeys.detail(scenarioId),
    queryFn: () => getScenario(scenarioId),
    enabled: !!scenarioId,
  });
}

/**
 * Create a new scenario.
 * Invalidates scenario lists on success.
 */
export function useCreateScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}

/**
 * Update a scenario (auto-increments version).
 * @param {string} scenarioId - UUID
 */
export function useUpdateScenario(scenarioId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateScenario(scenarioId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scenarioKeys.detail(scenarioId) });
    },
  });
}

/**
 * Delete or archive a scenario.
 */
export function useDeleteScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, archiveOnly }) => deleteScenario(scenarioId, archiveOnly),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}

/**
 * Fork (clone) a scenario for what-if analysis.
 */
export function useForkScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, newName }) => forkScenario(scenarioId, { new_name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}
